
import { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { platformConfigurations } from "./config/platformConfig";
import { ApiPlatformForm } from "./api/ApiPlatformForm";
import { useApiSettings } from "./api/useApiSettings";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";
import { useLocation } from "react-router-dom";
import { CredentialDebugPanel } from "./api/CredentialDebugPanel";

interface APISettingsProps {
  categoryFilter?: string;
}

// Category mappings
const CATEGORY_MAPPINGS: Record<string, string[]> = {
  recruiting: ["linkedin", "indeed", "dice-1", "zip"],
  ai: ["openai", "anthropic", "gemini", "mistral", "elevenlabs"],
  tools: ["resend", "scrapeless", "lutra-webhook"]
};

export const APISettings = ({ categoryFilter }: APISettingsProps) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const focusPlatform = searchParams.get("tab");

  const {
    isSubmitting,
    platformStates,
    loadingCredentials,
    handleInputChange,
    handleSaveCredentials,
    handleTestConnection,
    refreshCredentials
  } = useApiSettings();

  // Filter platforms based on category if provided
  const filteredPlatforms = categoryFilter 
    ? platformConfigurations.filter(p => CATEGORY_MAPPINGS[categoryFilter]?.includes(p.id))
    : platformConfigurations;

  // For debugging purposes
  useEffect(() => {
    console.log("Platform configurations:", filteredPlatforms.map(p => p.name));
    console.log("Platform states:", Object.keys(platformStates));
    console.log("Focus platform:", focusPlatform);
  }, [platformStates, filteredPlatforms, focusPlatform]);

  if (loadingCredentials) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>API Integrations</CardTitle>
          <CardDescription>Loading your saved configurations...</CardDescription>
        </CardHeader>
        <CardContent>
          <Progress value={50} className="w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {categoryFilter === "recruiting" ? "Recruiting Platforms" : 
             categoryFilter === "ai" ? "AI Services" : 
             categoryFilter === "tools" ? "Tools & Utilities" :
             "API Integrations"}
          </CardTitle>
          <CardDescription>
            {categoryFilter === "recruiting" ? "Configure your API credentials for job boards and recruiting platforms" : 
             categoryFilter === "ai" ? "Configure your API credentials for AI and machine learning services" : 
             categoryFilter === "tools" ? "Configure your API credentials for automation tools and utilities" :
             "Configure your API credentials for recruiting and AI platforms"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert variant="default" className="bg-blue-50 border-blue-200">
            <InfoIcon className="h-4 w-4 text-blue-600" />
            <AlertTitle>Displaying {filteredPlatforms.length} available integrations</AlertTitle>
            <AlertDescription>
              Configure the APIs you need for your recruiting workflow. Your API keys are stored securely and encrypted.
            </AlertDescription>
          </Alert>
          
          {filteredPlatforms.map((platform) => (
            <ApiPlatformForm
              key={platform.id}
              platform={platform}
              platformState={platformStates[platform.id] || { 
                enabled: false, 
                credentials: {}, 
                status: 'idle' 
              }}
              onInputChange={handleInputChange}
              onSaveCredentials={handleSaveCredentials}
              onTestConnection={handleTestConnection}
              isSubmitting={isSubmitting}
              isFocused={focusPlatform === platform.id}
            />
          ))}
        </CardContent>
      </Card>
      
      {/* Add debug panel for troubleshooting */}
      <CredentialDebugPanel />
    </>
  );
};
